
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import {
  IDocsCategoriesProviderData,
  IDocsCategoriesProviderMethods
} from "@/models/providers/docs";
import { IProvider } from "@/models/providers";
import { IDocsCategory } from "@/models/docs";

@Component({
  computed: {
    ...mapState({
      docsCategoriesScope(state: IState, getters) {
        return getters[`data/${DataModules.DOCUMENTATION}/categoriesScope`];
      },
      docsCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.DOCUMENTATION,
          scope: this.docsCategoriesScope
        });
      }
    })
  }
})
export default class DocsCategoriesProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props
  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Map State

  public docsCategoriesScope!: string;
  public docsCategories!: IDocsCategory[];

  // Data
  public isLoading = false;
  public isReloading = false;
  public hasRequestError = false;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.DOCUMENTATION,
      scope: this.docsCategoriesScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await this.getDocsCategories(this.isReloading);
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getDocsCategories(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.DOCUMENTATION}/listCategories`,
      {
        scope: this.docsCategoriesScope,
        vm: this,
        ignoreStored
      }
    );
  }

  // Guarding

  public guardFunctionalities = ["list_documentation_categories"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Render
  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $docsCategoriesData: this.providerData(),
        $docsCategoriesMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("docsCategoriesProviderData")
  public providerData(): IDocsCategoriesProviderData {
    return {
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      docsCategories: this.docsCategories
    };
  }

  // Provide Methods
  @Provide("docsCategoriesProviderMethods")
  public providerMethods: IDocsCategoriesProviderMethods = {
    reloadData: this.reloadData
  };
}
